import { Clock, CheckCircle2, XCircle, Loader2, X, Trash2 } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { useJobStore } from '@/store/jobStore'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Progress } from '@/components/ui/progress'
import type { Job } from '@/types/job'
import { formatDistanceToNow } from 'date-fns'
import { jobsApi } from '@/services/api/jobs'
import { showErrorToast, showSuccessToast } from '@/utils/error-handler'

function formatDuration(ms: number): string {
  if (ms < 1000) {
    return `${ms}ms`
  }
  if (ms < 60000) {
    return `${(ms / 1000).toFixed(1)}s`
  }
  if (ms < 3600000) {
    const minutes = Math.floor(ms / 60000)
    const seconds = Math.floor((ms % 60000) / 1000)
    return `${minutes}m ${seconds}s`
  }
  const hours = Math.floor(ms / 3600000)
  const minutes = Math.floor((ms % 3600000) / 60000)
  return `${hours}h ${minutes}m`
}

interface JobCardProps {
  job: Job
}

function getJobTypeLabel(jobType: string) {
  const labels: Record<string, string> = {
    compile: 'Compile',
    execute: 'Execute',
    backtest: 'Backtest',
    signals_analyze: 'Signals Analyze',
    multi_symbol_analyze: 'Multi-Symbol Analysis',
  }
  return labels[jobType] || jobType
}

function getStatusIcon(status: string) {
  switch (status) {
    case 'completed':
      return <CheckCircle2 className="w-4 h-4 text-green-600" />
    case 'failed':
      return <XCircle className="w-4 h-4 text-red-600" />
    case 'expired':
      return <XCircle className="w-4 h-4 text-orange-600" />
    case 'processing':
      return <Loader2 className="w-4 h-4 text-blue-600 animate-spin" />
    case 'queued':
      return <Clock className="w-4 h-4 text-gray-600" />
    default:
      return <Clock className="w-4 h-4" />
  }
}

function getStatusBadgeVariant(status: string) {
  const variants: Record<string, 'default' | 'secondary' | 'destructive'> = {
    completed: 'default',
    queued: 'secondary',
    processing: 'secondary',
    failed: 'destructive',
    cancelled: 'destructive',
    expired: 'destructive',
  }
  return variants[status] || 'secondary'
}

export default function JobCard({ job }: JobCardProps) {
  const navigate = useNavigate()
  const { cancelJob, removeJob } = useJobStore()

  const handleCancel = async (e: React.MouseEvent) => {
    e.stopPropagation()
    e.preventDefault()
    if (confirm('Are you sure you want to cancel this job?')) {
      await cancelJob(job.id)
    }
  }

  const handleDelete = async (e: React.MouseEvent) => {
    e.stopPropagation()
    e.preventDefault()
    if (confirm('Are you sure you want to delete this job? It will be permanently removed after 24 hours.')) {
      try {
        await jobsApi.delete(job.id)
        removeJob(job.id)
        showSuccessToast('Job deleted successfully')
        // Navigate to jobs list to avoid going to deleted job detail page
        navigate('/jobs')
      } catch (error) {
        showErrorToast(error, 'Failed to delete job')
      }
    }
  }

  const canCancel = job.status === 'queued' || job.status === 'processing'
  const canDelete = job.status === 'completed' || job.status === 'failed' || job.status === 'expired' || job.status === 'cancelled'

  const handleCardClick = (e: React.MouseEvent) => {
    // Don't navigate if clicking on buttons or links
    const target = e.target as HTMLElement
    if (target.closest('button') || target.closest('a')) {
      return
    }
    navigate(`/jobs/${job.id}`)
  }

  return (
    <Card className="hover:shadow-md transition-shadow cursor-pointer" onClick={handleCardClick}>
      <CardHeader>
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              {getStatusIcon(job.status)}
              <CardTitle className="text-lg">
                {getJobTypeLabel(job.jobType)}
                {job.script && (
                  <span className="ml-2 text-base font-normal text-muted-foreground">
                    • {job.script.name}
                  </span>
                )}
              </CardTitle>
              <Badge variant={getStatusBadgeVariant(job.status)}>
                {job.status}
              </Badge>
            </div>
            <CardDescription>
              Created {formatDistanceToNow(new Date(job.createdAt), { addSuffix: true })}
              {job.durationMs !== null && job.durationMs !== undefined && (
                <span className="ml-2">
                  • Duration: {formatDuration(job.durationMs)}
                </span>
              )}
              {job.dataset && (
                <span className="ml-2">
                  • Dataset: {job.dataset.name}
                </span>
              )}
            </CardDescription>
          </div>
          <div className="flex items-center gap-1">
            {canCancel && (
              <Button
                variant="ghost"
                size="icon"
                onClick={handleCancel}
                className="h-8 w-8"
                title="Cancel job"
              >
                <X className="w-4 h-4" />
              </Button>
            )}
            {canDelete && (
              <Button
                variant="ghost"
                size="icon"
                onClick={handleDelete}
                className="h-8 w-8 text-destructive hover:text-destructive"
                title="Delete job"
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {(job.status === 'processing' || job.status === 'queued') && (job.progress !== undefined || job.config?.progress !== undefined) && (
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Progress</span>
              <span>{job.progress ?? job.config?.progress ?? 0}%</span>
            </div>
            <Progress value={job.progress ?? job.config?.progress ?? 0} />
            {(job.progressMessage ?? job.config?.progressMessage) && (
              <p className="text-xs text-muted-foreground truncate">
                {job.progressMessage ?? job.config?.progressMessage}
              </p>
            )}
          </div>
        )}
        {job.error && (
          <div className="mt-2 p-2 bg-destructive/10 border border-destructive/20 rounded text-sm text-destructive">
            {job.error}
          </div>
        )}
        {job.resultId && (
          <div className="mt-2">
            <button
              onClick={(e) => {
                e.stopPropagation()
                navigate(`/results/${job.resultId}`)
              }}
              className="text-sm text-primary hover:underline bg-transparent border-none p-0 cursor-pointer"
            >
              View Results →
            </button>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
